/**
 * Operator Edit Page
 * Standalone page for editing operators with encrypted ID support
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { operatorApi } from '../../services/operatorApi'
import { decryptId } from '../../utils/encryption'
import OperatorForm from './OperatorForm'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import ErrorState from '../../components/common/ErrorState'
import { showError } from '../../utils/toast'

export default function OperatorEdit() {
  const { encryptedId } = useParams()
  const navigate = useNavigate()
  const [operator, setOperator] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Load operator data on mount and when encryptedId changes
  useEffect(() => {
    loadOperator()
  }, [encryptedId])

  async function loadOperator() {
    setLoading(true)
    setError(null)
    try {
      // Decrypt the ID from URL
      const id = decryptId(encryptedId)
      const data = await operatorApi.getOperator(id)
      setOperator(data?.operator || data)
    } catch (err) {
      console.error('Failed to load operator:', err)
      setError(err.message || 'Failed to load operator. Invalid or expired link.')
      showError('Failed to load operator')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (formData) => {
    setSaving(true)
    setError(null)
    try {
      const id = decryptId(encryptedId)
      await operatorApi.updateOperator(id, formData)
      // Navigate back to list after successful update
      navigate('/operator?view=operators')
    } catch (err) {
      setError(err.message)
      throw err // Re-throw to let form handle it
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    navigate('/operator?view=operators')
  }

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading operator..." />
  }

  if (error && !operator) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <ErrorState
            message={error}
            onRetry={loadOperator}
            retryText="Try Again"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Edit Operator</h1>
          
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {operator && (
            <OperatorForm
              initialData={operator}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isEdit={true}
            />
          )}
        </div>
      </div>
    </div>
  )
}
